import { createSlice, EntityAdapter, EntityState, PayloadAction } from '@reduxjs/toolkit';
import { keyBy } from 'lodash-es';
import { uniqPrimativeMerge } from '../../utils/arrayUtils';
import { lookupEntityAdapters } from './lookupsEntityAdapters';
import * as i from './lookupsInterfaces';
import * as utils from './lookupStateUtils';

export interface ILookupState {
  addressTypes: i.ILookup<string>;
  bigCommercePricingTypes: i.IItem<i.IBigCommercePricingType>;
  brands: i.IStringOptionsWithOriginal<string, i.IBrand>;
  businessEntityRankTypes: i.IItem<i.IBusinessEntityRankType>;
  businessTypes: i.ILookup<string>;
  cashDiscountCategories: i.IItem<i.ICashDiscountCategory>;
  cashDiscountTypes: i.IItem<i.ICashDiscountType>;
  cashDiscountValueTypes: i.IItem<i.ICashDiscountValueType>;
  companyDimensions: EntityState<i.ICompanyDimension>;
  coOpCampaignTypes: i.ILookup<i.ICoOpCampaignType>;
  coOpStates: i.ILookup<i.ICoOpState>;
  competitors: i.IItem<i.ICompetitor>;
  counties: { [index: string]: i.ICountyLookup[] };
  countiesLoading: { counties: string[] };
  customerClassifications: i.IStringItem<i.ICustomerClassification>;
  deliveryModes: i.IStringItem<i.IDeliveryMode>;
  departments: i.IItem<i.IDepartment>;
  displayTypes: i.ILookup<i.IDisplayType>;
  distributors: i.IStringItem<i.IDistributor>;
  dpNewsFeedAccessFilterTypes: i.IItem<i.IDpNewsFeedAccessFilterType>;
  dpNewsFeedAccessTypes: i.IItem<i.IDpNewsFeedAccessType>;
  dpNewsFeedPostStatuses: i.IItem<i.IDpNewsFeedPostStatus>;
  dpNewsFeedTags: i.IItem<i.IDpNewsFeedTag>;
  dpShipMethods: EntityState<i.IDpShipMethod>;
  jobTitles: string[];
  loadDateChangeReasons: i.IItem<i.ILoadDateChangeReasons>;
  loadFeeRateTypes: i.IItem<i.ILoadFeeRateType>;
  loadFeeTypes: i.IItem<i.ILoadFeeType>;
  loadStates: i.ILookup<i.ILoadState>;
  loadTypes: i.IItem<i.ILoadType>;
  loaded: i.OnDemandLookupTypes[];
  loading: i.OnDemandLookupTypes[];
  logisticsFileTypes: i.IItem<i.ILogisticsFileType>;
  logitsicsThresholds: i.IItem<i.ILogisticsThreshold>;
  marketSegments: i.IItem<i.IMarketSegment>;
  negotiatingTactics: i.IItem<i.INegotiatingTactic>;
  newDealerRequestCompleteEventTypes: i.IItem<i.INewDealerRequestCompleteEventType>;
  newDealerRequestEventTypes: i.IItem<i.INewDealerRequestEventType>;
  newDealerRequestStates: i.ILookup<i.INewDealerRequestState>;
  paymentOptions: i.IItem<i.IPaymentOption>;
  paymentTerms: string[];
  pickLists: EntityState<i.IPickList>;
  priceDiscountGroups: i.IPriceDiscountGroup[];
  productCategories: i.IItem<i.IProductCategory>;
  productFamilies: i.IStringItem<i.IProductFamily>;
  prospectLifeCycleSteps: i.ILookup<i.IProspectLifecycleStep>;
  prospectStatus: i.ILookup<string>;
  purposeCodes: i.IStringItem<i.IPurposeCode>;
  regions: i.ILookup<i.IRegion>;
  salesCallMethods: i.INumOptions<string>;
  salesCallObjectives: i.IItem<i.ISalesCallObjective>;
  salesCallTypes: i.IItem<i.ISalesCallType>;
  salesDistricts: i.IStringLookup<i.ISalesDistrict>;
  salesOrigins: i.IStringItem<i.ISalesOrigin>;
  sourcesOfPower: i.IItem<i.ISourceOfPower>;
  shipmentContainerTypes: i.IItem<i.IShipmentContainerType>;
  sqlClauses: i.IItem<i.ISqlClause>;
  states: { ids: number[]; hash: { [index: number]: i.IStateLookup }; fipsHash: { [index: string]: string } };
  trailerTypes: i.IItem<i.ITrailerType>;
  valueStreams: i.IStringItem<i.IValueStream>;
  warehouseGroups: i.IItem<i.IWarehouseGroup>;
  warehouses: i.IStringItem<i.IWarehouse>;
}

const initialState: ILookupState = {
  addressTypes: { ids: [], hash: {} },
  bigCommercePricingTypes: { hash: {}, items: [] },
  brands: { options: [], hash: {}, values: [] },
  businessEntityRankTypes: { hash: {}, items: [] },
  businessTypes: { ids: [], hash: {} },
  cashDiscountCategories: { hash: {}, items: [] },
  cashDiscountTypes: { hash: {}, items: [] },
  cashDiscountValueTypes: { hash: {}, items: [] },
  companyDimensions: lookupEntityAdapters.companyDimensions.getInitialState(),
  competitors: { hash: {}, items: [] },
  coOpCampaignTypes: { ids: [], hash: {} },
  coOpStates: { ids: [], hash: {} },
  counties: {},
  countiesLoading: { counties: [] },
  customerClassifications: { hash: {}, items: [] },
  deliveryModes: { hash: {}, items: [] },
  departments: { hash: {}, items: [] },
  displayTypes: { hash: {}, ids: [] },
  distributors: { hash: {}, items: [] },
  dpNewsFeedAccessFilterTypes: { hash: {}, items: [] },
  dpNewsFeedAccessTypes: { hash: {}, items: [] },
  dpNewsFeedPostStatuses: { hash: {}, items: [] },
  dpNewsFeedTags: { hash: {}, items: [] },
  dpShipMethods: lookupEntityAdapters.dpShipMethods.getInitialState(),
  jobTitles: [],
  loadDateChangeReasons: { hash: {}, items: [] },
  loaded: [],
  loadFeeRateTypes: { hash: {}, items: [] },
  loadFeeTypes: { hash: {}, items: [] },
  loading: [],
  loadStates: { hash: {}, ids: [] },
  loadTypes: { hash: {}, items: [] },
  logisticsFileTypes: { hash: {}, items: [] },
  logitsicsThresholds: { hash: {}, items: [] },
  marketSegments: { hash: {}, items: [] },
  negotiatingTactics: { items: [], hash: {} },
  newDealerRequestCompleteEventTypes: { hash: {}, items: [] },
  newDealerRequestEventTypes: { hash: {}, items: [] },
  newDealerRequestStates: { hash: {}, ids: [] },
  paymentOptions: { hash: {}, items: [] },
  paymentTerms: [],
  pickLists: lookupEntityAdapters.pickLists.getInitialState(),
  priceDiscountGroups: [],
  productCategories: { items: [], hash: {} },
  productFamilies: { hash: {}, items: [] },
  prospectLifeCycleSteps: { ids: [], hash: {} },
  prospectStatus: { ids: [], hash: {} },
  purposeCodes: { hash: {}, items: [] },
  regions: { hash: {}, ids: [] },
  salesCallMethods: { options: [], hash: {} },
  salesCallObjectives: { items: [], hash: {} },
  salesCallTypes: { items: [], hash: {} },
  salesDistricts: { ids: [], hash: {} },
  salesOrigins: { hash: {}, items: [] },
  shipmentContainerTypes: { hash: {}, items: [] },
  sourcesOfPower: { items: [], hash: {} },
  sqlClauses: { hash: {}, items: [] },
  states: { ids: [], hash: {}, fipsHash: {} },
  trailerTypes: { hash: {}, items: [] },
  valueStreams: { hash: {}, items: [] },
  warehouseGroups: { hash: {}, items: [] },
  warehouses: { hash: {}, items: [] }
};

// tslint:disable: no-empty
const lookupsSlice = createSlice({
  initialState,
  name: 'lookups',
  reducers: {
    deleteLookupValue: (state, action: PayloadAction<{ type: keyof i.ILookupApiResponse; id: number }>) => {},
    fetchCounties: {
      prepare: (stateFipsId: string) => ({ payload: { stateFipsId } }),
      reducer: (state, action: PayloadAction<{ stateFipsId: string }>) => {
        state.countiesLoading.counties = uniqPrimativeMerge(state.countiesLoading.counties, action.payload.stateFipsId);
      }
    },
    fetchOnDemandLookups: {
      prepare: (lookupTypes: i.OnDemandLookupTypes[]) => ({ payload: { types: lookupTypes } }),
      reducer: (state, action: PayloadAction<{ types: i.OnDemandLookupTypes[] }>) => {
        state.loading = uniqPrimativeMerge(state.loading, action.payload.types);
      }
    },
    removeLoaded: {
      prepare: (type: i.OnDemandLookupTypes[]) => ({ payload: { type } }),
      reducer: (state, action: PayloadAction<{ type: i.OnDemandLookupTypes[] }>) => {
        state.loaded = state.loaded.filter(x => !action.payload.type.includes(x));
      }
    },
    removeLoading: {
      prepare: (type: i.OnDemandLookupTypes[]) => ({ payload: { type } }),
      reducer: (state, action: PayloadAction<{ type: i.OnDemandLookupTypes[] }>) => {
        state.loading = state.loading.filter(x => !action.payload.type.includes(x));
      }
    },
    removeLookupValue: (state, action: PayloadAction<{ type: keyof i.ILookupApiResponse; id: number }>) => {
      switch (action.payload.type) {
        case 'businessEntityRankTypes':
          state.businessEntityRankTypes = utils.processRemove(
            state.businessEntityRankTypes,
            action.payload.id,
            x => x.businessEntityRankTypeId
          );
          break;
        case 'logisticsThresholds':
          state.logitsicsThresholds = utils.processRemove(
            state.logitsicsThresholds,
            action.payload.id,
            x => x.logisticsThresholdId
          );
        case 'marketSegments':
          state.marketSegments = utils.processRemove(state.marketSegments, action.payload.id, x => x.id);
          break;
        case 'negotiatingTactics':
          state.negotiatingTactics = utils.processRemove(
            state[action.payload.type],
            action.payload.id,
            x => x.negotiatingTacticId
          );
          break;
        case 'productCategories':
          state.productCategories = utils.processRemove(state.productCategories, action.payload.id, x => x.categoryId);
          break;
        case 'regions':
          state.regions = utils.removeKey(state.regions, action.payload.id);
        case 'sourcesOfPower':
          state.sourcesOfPower = utils.processRemove(state.sourcesOfPower, action.payload.id, x => x.sourceOfPowerId);
          break;
        case 'warehouseGroups':
          state.warehouseGroups = utils.processRemove(
            state[action.payload.type],
            action.payload.id,
            x => x.warehouseGroupId
          );
          break;
        default:
          break;
      }
    },
    setCounties: {
      prepare: (stateFipsId: string, items: i.ICountyLookup[]) => ({ payload: { stateFipsId, items } }),
      reducer: (state, action: PayloadAction<{ stateFipsId: string; items: i.ICountyLookup[] }>) => {
        state.counties = { ...state.counties, [action.payload.stateFipsId]: action.payload.items };
      }
    },
    setLookups: (state, action: PayloadAction<{ payload: i.ILookupApiResponse; partial?: boolean }>) => {
      const { payload } = action.payload;

      const loaded = Object.entries(payload)
        .filter(([, value]) => Boolean(value)) // check if there are values to the key
        .map(([key]) => key as keyof i.ILookupApiResponse);

      state.loading = state.loading.filter(x => !loaded.includes(x));

      if (!action.payload.partial) {
        // only mark as loaded if it is not a partial update
        state.loaded = uniqPrimativeMerge(state.loaded, loaded);
      }

      if (action.payload.payload) {
        Object.keys(action.payload.payload).forEach(key => {
          // cast the string key to an IEntity
          const typedKey = key as keyof typeof lookupEntityAdapters;
          // get the entity adapter, type it as unknown because we don't know what it is
          const adapter = lookupEntityAdapters[typedKey] as EntityAdapter<unknown>;

          if (adapter && adapter.updateMany) {
            // this is disabled because we are iterating over the object so we know the value exists
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            adapter.upsertMany(state[typedKey], action.payload.payload[typedKey]!);
          }
        });
      }

      state.addressTypes = utils.processString(
        payload.addressTypes,
        state.addressTypes,
        x => x.addressTypeId,
        x => x.type
      );
      state.bigCommercePricingTypes = utils.processItem(
        payload.bigCommercePricingTypes,
        state.bigCommercePricingTypes,
        x => x.bigCommercePricingTypeId,
        x => x.pricingType
      );
      state.brands = {
        ...utils.processOptionsStringByStrId(
          payload.brands,
          state.brands,
          x => x.abbreviation,
          x => x.brand
        ),
        values: payload.brands ? payload.brands : state.brands.values
      };
      state.businessEntityRankTypes = utils.processItem(
        payload.businessEntityRankTypes,
        state.businessEntityRankTypes,
        x => x.businessEntityRankTypeId,
        x => x.name
      );
      state.businessTypes = utils.processString(
        payload.businessTypes,
        state.businessTypes,
        x => x.id,
        x => x.type
      );
      state.cashDiscountCategories = utils.processItem(
        payload.cashDiscountCategories,
        state.cashDiscountCategories,
        x => x.programCashDiscountCategoryId,
        x => x.categoryName
      );
      state.cashDiscountTypes = utils.processItem(
        payload.cashDiscountTypes,
        state.cashDiscountTypes,
        x => x.programCashDiscountTypeId,
        x => x.type
      );
      state.cashDiscountValueTypes = utils.processItem(
        payload.cashDiscountValueTypes,
        state.cashDiscountValueTypes,
        x => x.programCashDiscountValueTypeId,
        x => x.type
      );
      state.competitors = utils.processItem(
        payload.competitors,
        state.competitors,
        x => x.id,
        x => x.name
      );
      state.coOpCampaignTypes = utils.processILookupObj(
        payload.coOpCampaignTypes,
        state.coOpCampaignTypes,
        x => x.coOpCampaignTypeId
      );
      state.coOpStates = utils.processILookupObj(payload.coOpStates, state.coOpStates, x => x.coOpStateId);
      state.customerClassifications = utils.processStringItem(
        payload.customerClassifications,
        state.customerClassifications,
        x => x.custClassificationId,
        x => x.description
      );
      state.deliveryModes = utils.processStringItem(
        payload.deliveryModes,
        state.deliveryModes,
        x => x.deliveryModeId,
        x => x.description
      );
      state.departments = utils.processItem(
        payload.departments,
        state.departments,
        x => x.shoreMasterDepartmentId,
        x => x.name
      );
      state.displayTypes = utils.processILookupObj<i.IDisplayType>(
        payload.displayTypes,
        state.displayTypes,
        x => x.displayTypeId
      );
      state.distributors = utils.processStringItem(
        payload.distributors,
        state.distributors,
        x => x.accountNum,
        x => x.accountNum
      );
      state.dpNewsFeedAccessFilterTypes = utils.processItem(
        payload.dpNewsFeedAccessFilterTypes,
        state.dpNewsFeedAccessFilterTypes,
        x => x.dpNewsFeedAccessFilterTypeId,
        x => x.type
      );
      state.dpNewsFeedAccessTypes = utils.processItem(
        payload.dpNewsFeedAccessTypes,
        state.dpNewsFeedAccessTypes,
        x => x.dpNewsFeedAccessTypeId,
        x => x.type
      );
      state.dpNewsFeedPostStatuses = utils.processItem(
        payload.dpNewsFeedPostStatuses,
        state.dpNewsFeedPostStatuses,
        x => x.dpNewsFeedPostStatusId,
        x => x.status
      );
      state.dpNewsFeedTags = utils.processItem(
        payload.dpNewsFeedTags,
        state.dpNewsFeedTags,
        x => x.dpNewsFeedTagId,
        x => x.name
      );
      state.jobTitles = payload.jobTitles ? payload.jobTitles : state.jobTitles;
      state.loadDateChangeReasons = utils.processItem(
        payload.loadDateChangeReasons,
        state.loadDateChangeReasons,
        x => x.loadDateChangeReasonId,
        x => x.reason
      );
      state.loadFeeRateTypes = utils.processItem(
        payload.loadFeeRateTypes,
        state.loadFeeRateTypes,
        x => x.loadFeeRateTypeId,
        x => x.type
      );
      state.loadFeeTypes = utils.processItem(
        payload.loadFeeTypes,
        state.loadFeeTypes,
        x => x.loadFeeTypeId,
        x => x.type
      );
      state.loadStates = utils.processILookupObj(payload.loadStates, state.loadStates, x => x.loadStateId);
      state.loadTypes = utils.processItem(
        payload.loadTypes,
        state.loadTypes,
        x => x.loadTypeId,
        x => x.name
      );
      state.logisticsFileTypes = utils.processItem(
        payload.logisticsFileTypes,
        state.logisticsFileTypes,
        x => x.logisticsFileTypeId,
        x => x.title
      );
      state.logitsicsThresholds = utils.processItem(
        payload.logisticsThresholds,
        state.logitsicsThresholds,
        x => x.logisticsThresholdId,
        x => x.backgroundColor
      );
      state.marketSegments = utils.processItem(
        payload.marketSegments,
        state.marketSegments,
        x => x.id,
        x => x.segment
      );
      state.negotiatingTactics = utils.processItem(
        payload.negotiatingTactics,
        state.negotiatingTactics,
        x => x.negotiatingTacticId,
        x => x.tactic
      );
      state.newDealerRequestCompleteEventTypes = utils.processItem(
        payload.newDealerRequestCompleteEventTypes,
        state.newDealerRequestCompleteEventTypes,
        x => x.newDealerRequestCompleteEventTypeId,
        x => x.type
      );
      state.newDealerRequestEventTypes = utils.processItem(
        payload.newDealerRequestEventTypes,
        state.newDealerRequestEventTypes,
        x => x.newDealerRequestEventTypeId,
        x => x.type
      );
      state.newDealerRequestStates = utils.processILookupObj(
        payload.newDealerRequestStates,
        state.newDealerRequestStates,
        x => x.newDealerRequestStateId
      );
      state.paymentOptions = utils.processItem(
        payload.paymentOptions,
        state.paymentOptions,
        x => x.id,
        x => x.option
      );
      state.paymentTerms = payload.paymentTerms ? payload.paymentTerms : state.paymentTerms;
      state.priceDiscountGroups = payload.priceDiscountGroups ? payload.priceDiscountGroups : state.priceDiscountGroups;
      state.productCategories = utils.processItem(
        payload.productCategories,
        state.productCategories,
        x => x.categoryId,
        x => x.category
      );
      state.productFamilies = utils.processStringItem(
        payload.productFamilies,
        state.productFamilies,
        x => x.productFamilyId,
        x => x.description
      );
      state.prospectLifeCycleSteps = utils.processILookupObj(
        payload.prospectLifeCycle,
        state.prospectLifeCycleSteps,
        x => x.prospectLifeCycleStepId
      );
      state.prospectStatus = utils.processString(
        payload.prospectStatus,
        state.prospectStatus,
        x => x.statusId,
        x => x.status
      );
      state.purposeCodes = utils.processStringItem(
        payload.purposeCodes,
        state.purposeCodes,
        x => x.purposeCodeId,
        x => x.description
      );
      state.regions = utils.processILookupObj(payload.regions, state.regions, x => x.regionId);
      state.salesCallMethods = utils.processOptionsStringByNumId(
        payload.salesCallMethods,
        state.salesCallMethods,
        x => x.salesCallMethodId,
        x => x.method
      );
      state.salesCallObjectives = utils.processItem(
        payload.salesCallObjectives,
        state.salesCallObjectives,
        x => x.objectiveId,
        x => x.objective
      );
      state.salesCallTypes = utils.processItem(
        payload.salesCallTypes,
        state.salesCallTypes,
        x => x.salesCallTypeId,
        x => x.type
      );
      state.salesDistricts = utils.processObjectStr(
        payload.salesDistricts,
        state.salesDistricts,
        x => x.salesDistrictId
      );
      state.salesOrigins = utils.processStringItem(
        payload.salesOrigins,
        state.salesOrigins,
        x => x.originId,
        x => x.description
      );
      state.shipmentContainerTypes = utils.processItem(
        payload.shipmentContainerTypes,
        state.shipmentContainerTypes,
        x => x.shipmentContainerTypeId,
        x => x.name
      );
      state.sourcesOfPower = utils.processItem(
        payload.sourcesOfPower,
        state.sourcesOfPower,
        x => x.sourceOfPowerId,
        x => x.title
      );
      state.sqlClauses = utils.processItem(
        payload.sqlClauses,
        state.sqlClauses,
        x => x.sqlClauseId,
        x => x.clause
      );
      state.states = payload.states
        ? {
            fipsHash: payload.states.reduce<{ [index: string]: string }>((hash, s) => {
              hash[s.fipsId] = s.name;
              return hash;
            }, {}),
            hash: keyBy(payload.states, (s: i.IStateLookup) => s.id),
            ids: payload.states.map(x => x.id)
          }
        : state.states;
      state.trailerTypes = utils.processItem(
        payload.trailerTypes,
        state.trailerTypes,
        x => x.trailerTypeId,
        x => x.name
      );
      state.valueStreams = utils.processStringItem(
        payload.valueStreams,
        state.valueStreams,
        x => x.valueStreamId,
        x => x.description
      );
      state.warehouseGroups = utils.processItem(
        payload.warehouseGroups,
        state.warehouseGroups,
        x => x.warehouseGroupId,
        x => x.name
      );
      state.warehouses = utils.processStringItem(
        payload.warehouses,
        state.warehouses,
        x => x.warehouseId,
        x => x.name
      );
    },
    updateValue: <T>(state: ILookupState, action: PayloadAction<{ type: keyof i.ILookupApiResponse; value: T }>) => {}
  }
});

export const { actions: lookupActionCreators, reducer: lookupsReducer } = lookupsSlice;
